import { toEnglishDigits } from 'utilities/helper/toEnglishNumber'

/**
 * Normalise whatever we are handed into the `09xxxxxxxxx` form.
 *
 * The same number reaches us in three different spellings depending on where
 * it came from: the login field gets `09xxxxxxxxx` typed by the user, the
 * backend stores and returns `+989xxxxxxxxx`, and a messenger's shared contact
 * can send either, sometimes with a `00` prefix. Anything that compares or
 * keys on a phone number has to pick one spelling first, or it will treat one
 * person as two.
 *
 * Returns null for anything that is not a plausible Iranian mobile number;
 * callers must treat null as "we do not have a usable number".
 */
export const toLocalMobile = (raw: string | null | undefined): string | null => {
    // Fold first: `\D` counts «۰۹۱۲…» as non-digits and would strip a
    // Persian-typed number down to the empty string.
    let digits = toEnglishDigits(raw || '').replace(/\D/g, '')
    if (digits.startsWith('0098')) digits = digits.slice(4)
    if (digits.startsWith('0')) digits = digits.slice(1)
    // Length-guarded so a bare 10-digit number is never mistaken for a country
    // code — no Iranian mobile is 98xxxxxxxx, but don't rely on that.
    if (digits.length > 10 && digits.startsWith('98')) digits = digits.slice(2)
    return /^9\d{9}$/.test(digits) ? '0' + digits : null
}

/** True when two differently-spelled numbers are the same subscriber. */
export const isSameMobile = (
    a: string | null | undefined,
    b: string | null | undefined
): boolean => {
    const left = toLocalMobile(a)
    return !!left && left === toLocalMobile(b)
}
